// src/components/TaskExitCards/TaskExitCards.js
import React, { useMemo, useState, useCallback } from "react";
import styles from "./TaskExitCards.module.css";

import WarningIcon from "../../assets/svgs/pendingWarning.svg";
import WarningGreenIcon from "../../assets/svgs/greenWarning.svg";

import NotesBlue from "../../assets/svgs/notesBlue.svg";
import NotesBlack from "../../assets/svgs/notesBlack.svg";
import DescriptionHighlight from "../../assets/svgs/descriptionHighlight.svg";
import DescriptionIcon from "../../assets/svgs/descriptionIcon.svg";

const TaskExitCards = ({ dayData = {}, isMobile = false, isLoading = false }) => {
  const userNames = useMemo(
    () => Object.keys(dayData).sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base" })),
    [dayData]
  );

  const [expanded, setExpanded] = useState({});

  const keyFor = useCallback((t, idx, owner) => String(t.id ?? `${owner}-${idx}`), []);

  const toggleDesc = (k) => {
    setExpanded((prev) => {
      const cur = prev[k] || { desc: false, notes: false };
      return { ...prev, [k]: { desc: !cur.desc, notes: false } };
    });
  };
  const toggleNotes = (k) => {
    setExpanded((prev) => {
      const cur = prev[k] || { desc: false, notes: false };
      return { ...prev, [k]: { desc: false, notes: !cur.notes } };
    });
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner} aria-label="Cargando salidas..." />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {userNames.map((name) => {
        const tasks = dayData[name] || [];
        const avatar = tasks.find((t) => t.Avatar)?.Avatar || "";

        return (
          <div key={name} className={styles.userBlock}>
            <div className={styles.userHeader}>
              <div className={styles.avatarWrap}>
                {avatar ? (
                  <img src={avatar} alt={name} className={styles.avatarImg} />
                ) : (
                  <div className={styles.avatarFallback}>
                    {name?.[0]?.toUpperCase() || "?"}
                  </div>
                )}
              </div>
              <span className={styles.userName}>{name}</span>
            </div>

            <div className={styles.cardsList}>
              {tasks.map((t, idx) => {
                const origen = Number(t.Origen || 0);
                const idEmpleado = Number(t.IDEmpleado || 0);
                const isPending = origen === -idEmpleado;
                const isAssignedFromOther =
                  origen !== 0 && origen !== idEmpleado && origen !== -idEmpleado;
                const comparte = Array.isArray(t.Comparte) ? t.Comparte : [];

                const k = keyFor(t, idx, name);
                const showDesc = !!expanded[k]?.desc;
                const showNotes = !!expanded[k]?.notes;

                return (
                  <div className={styles.card} key={`${k}-${name}`}>
                    <div className={styles.leftBar} />
                    <div className={styles.cardBody}>
                      {(comparte.length > 0 || isPending || isAssignedFromOther) && (
                        <div className={styles.badgesRow}>
                          {isPending && (
                            <div className={styles.pendingBadge}>
                              <img src={WarningIcon} alt="Warning" className={styles.pendingIcon} />
                              <span className={styles.pendingText}>Solicitud pendiente</span>
                            </div>
                          )}

                          {isAssignedFromOther && (
                            <div className={styles.originBadge}>
                              <img src={WarningGreenIcon} alt="Advertencia" className={styles.originIcon} />
                              <span className={styles.originText}>
                                Tarea de {t.EmpleadoOrigen || "otro usuario"} asignada
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Título */}
                      <p className={styles.titleLine}>
                        <span className={styles.titleStrong}>Salidas</span>{" "}
                        <span className={styles.titleSpan}>Cobertura para </span>
                        <span className={styles.titleRest}>{t.Salida || ""}</span>
                      </p>

                      {/* Tiempo */}
                      {t.Tiempo ? <p className={styles.timeLine}>{t.Tiempo}</p> : null}

                      {/* Desktop: descripción / notas visibles si existen */}
                      {!isMobile && t.Descripcion?.trim() && (
                        <p className={styles.descLine}>
                          <span className={styles.italicLabel}>Descripción:</span> {t.Descripcion}
                        </p>
                      )}
                      {!isMobile && t.Nota?.trim() && (
                        <p className={styles.noteLine}>
                          <span className={styles.noteLabel}>Nota:</span>{" "}
                          <span className={styles.noteText}>{t.Nota}</span>
                        </p>
                      )}

                      {isMobile && (t.Descripcion?.trim() || t.Nota?.trim()) && (
                        <>
                          {showDesc && t.Descripcion?.trim() && (
                            <p className={styles.descLine} style={{ marginTop: 8 }}>
                              {t.Descripcion}
                            </p>
                          )}
                          {showNotes && t.Nota?.trim() && (
                            <p className={styles.noteLine} style={{ marginTop: 8 }}>
                              {t.Nota}
                            </p>
                          )}

                          <div style={{ display: "flex", gap: 12, marginTop: 6 }}>
                            {t.Descripcion?.trim() && (
                              <button
                                type="button"
                                onClick={() => toggleDesc(k)}
                                className={styles.iconButton}
                                style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: 0, padding: 0, cursor: "pointer" }}
                              >
                                <img src={showDesc ? DescriptionHighlight : DescriptionIcon} alt="Descripción" />
                                <span className={`${styles.iconText} ${showDesc ? styles.highlightText : ""}`}>
                                  Descripción
                                </span>
                              </button>
                            )}

                            {t.Nota?.trim() && (
                              <button
                                type="button"
                                onClick={() => toggleNotes(k)}
                                className={styles.iconButton}
                                style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: 0, padding: 0, cursor: "pointer" }}
                              >
                                <img src={showNotes ? NotesBlue : NotesBlack} alt="Notas" />
                                <span className={`${styles.iconText} ${showNotes ? styles.highlightText : ""}`}>
                                  Notas
                                </span>
                              </button>
                            )}
                          </div>
                        </>
                      )}

                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TaskExitCards;
